import {Component, EventEmitter, Input, Output} from '@angular/core';
import {Service} from '../../../../models/service';
import {RegisterCompanyStepBase} from '../register-company-step.base';

@Component({
    selector: 'app-register-company-services',
    templateUrl: './services-step.component.html',
    styleUrls: ['../../register-company.component.scss']
})
export class RegisterCompanyServicesStepComponent extends RegisterCompanyStepBase {

    @Input() services: Service[] = [];
    @Input() servicesSelected: Service[] = [];
    @Input() billingPeriod: 'monthly' | 'yearly' = 'monthly';
    @Input() tariffTiers: { range: string; rate: string }[] = [];

    @Output() billingPeriodChange = new EventEmitter<'monthly' | 'yearly'>();
    @Output() planSelected = new EventEmitter<Service>();

    submitted = false;

    get hasSelectedPlan(): boolean {
        return this.servicesSelected.length > 0;
    }

    setBillingPeriod(period: 'monthly' | 'yearly'): void {
        this.billingPeriod = period;
        this.billingPeriodChange.emit(period);
        this.persistForm();
    }

    selectPlan(service: Service): void {
        this.planSelected.emit(service);
    }

    tryContinue(): void {
        this.submitted = true;
        if (!this.hasSelectedPlan) return;
        this.nextStep();
    }

    setCheckbox(serviceCheckbox: Service) {
        return this.servicesSelected.find((service: Service) => serviceCheckbox.id === service.id);
    }

    getFeatureLabel(feature: string | { name?: string; code?: string } | unknown): string {
        if (feature == null) return '';
        if (typeof feature === 'string') return feature;
        const o = feature as { name?: string; code?: string };
        return o?.name ?? o?.code ?? String(feature);
    }

    getDisplayedMonthlyPrice(service: Service): number {
        const base = parseFloat(service.price || '0');
        if (this.billingPeriod === 'monthly') {
            return base;
        }
        return parseFloat(service.annualPrice || '0');
    }

    getAnnualBilledMonthlyPrice(service: Service): number {
        const annual = parseFloat(service.annualPrice || '0');
        const recurrencyMonths = this.getServiceRecurrencyMonths(service);
        return Number.isNaN(annual) ? 0 : annual / recurrencyMonths;
    }

    getServiceRecurrencyMonths(service: Service): number {
        const raw = Number((service as any)?.recurrency);
        return Number.isFinite(raw) && raw > 0 ? raw : 12;
    }

    hasCustomSubscriptionTerms(service: Service): boolean {
        return service.customFeeRate != null || service.monthCount != null;
    }

    shouldHideStandardTariffTable(): boolean {
        const s = this.company.service;
        return !!s && this.hasCustomSubscriptionTerms(s);
    }

    getAnnualSavingsPercent(service: Service): number | null {
        const monthly = this.parsePrice(service.price);
        const annual = this.parsePrice(service.annualPrice);
        if (Number.isNaN(monthly) || Number.isNaN(annual) || monthly <= 0) {
            return null;
        }
        const fullYearAtMonthly = monthly * this.getServiceRecurrencyMonths(service);
        if (fullYearAtMonthly <= 0) {
            return null;
        }
        const pct = ((fullYearAtMonthly - annual) / fullYearAtMonthly) * 100;
        if (pct <= 0.05) {
            return null;
        }
        return Math.round(pct * 10) / 10;
    }

    private parsePrice(raw: string | undefined | null): number {
        const n = parseFloat(String(raw ?? '').replace(/\s/g, '').replace(',', '.'));
        return Number.isNaN(n) ? NaN : n;
    }
}
